import icon from '../icon';
import ServiceWorkerRouter from './ServiceWorkerRouter';
import type { Occurrence, TrailData } from '../apiConfigs/@types';
import { booleanPointInPolygon, featureCollection, point } from '@turf/turf';
import dayjs from 'dayjs';

const router = new ServiceWorkerRouter(`service.jsdc.com.tw`, 'https:');

const memCache = new Map();

enum CacheKey {
  COUNTY = 'county.geojson',
  TOWN = 'town.geojson',
  TRAILS = 'trails.json',
  ADVANCE_LISTING = 'advance_listing.json',
}

const IRIVER_MAX_CONCURRENCY = 4;
let iriverActive = 0;
const iriverQueue: Array<() => void> = [];

function isIriverUrl(url: string): boolean {
  return url.includes('iriver.tw');
}

async function iriverFetch(url: string): Promise<Response> {
  if (!isIriverUrl(url)) {
    return fetch(url);
  }

  if (iriverActive >= IRIVER_MAX_CONCURRENCY) {
    await new Promise<void>((resolve) => iriverQueue.push(resolve));
  }

  iriverActive++;
  try {
    return await fetch(url);
  } finally {
    iriverActive--;
    const next = iriverQueue.shift();
    if (next) next();
  }
}

function withOffset(url: string, offset: number): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}page%5Boffset%5D=${offset}`;
}

async function fetchPage(url: string): Promise<{ data: any[]; included: any[]; meta: any }> {
  const response = await iriverFetch(url);
  const rawData: any = await response.json();
  return {
    data: Array.isArray(rawData.data) ? rawData.data : [],
    included: Array.isArray(rawData.included) ? rawData.included : [],
    meta: rawData.meta,
  };
}

async function fetchPaginatedListing(
  initialUrl: string,
  options: { initialOffsets?: number[] } = {},
): Promise<{ data: any[]; includedById: Map<string, any> }> {
  const initialOffsets = options.initialOffsets ?? [0];
  const data: any[] = [];
  const includedById = new Map<string, any>();

  const firstBatch = await Promise.all(
    initialOffsets.map((offset) =>
      fetchPage(offset === 0 ? initialUrl : withOffset(initialUrl, offset)),
    ),
  );

  let meta: any;
  for (const page of firstBatch) {
    data.push(...page.data);
    for (const item of page.included) includedById.set(item.id, item);
    if (page.meta && meta === undefined) meta = page.meta;
  }

  const total: number | undefined = meta?.count;
  const pageSize = firstBatch[0]?.data.length ?? 0;
  const highestOffsetTried = Math.max(...initialOffsets);
  const coveredUpTo = highestOffsetTried + pageSize;

  if (typeof total === 'number' && pageSize > 0 && coveredUpTo < total) {
    const restOffsets: number[] = [];
    for (let offset = coveredUpTo; offset < total; offset += pageSize) restOffsets.push(offset);

    const restPages = await Promise.all(
      restOffsets.map((offset) => fetchPage(withOffset(initialUrl, offset))),
    );

    for (const page of restPages) {
      data.push(...page.data);
      for (const item of page.included) includedById.set(item.id, item);
    }
  }

  return { data, includedById };
}

function mapListingItemToOccurrence(
  element: any,
  includedById: Map<string, any>,
  // trails 用 field_listing_category；advance_listing 的分類放在 field_listing_category_reference
  categoryField: string = 'field_listing_category',
): Occurrence {
  const imageId = element.relationships?.field_listing_image?.data?.id;
  const imageEntry = imageId ? includedById.get(imageId) : null;
  const occurrencePhotoUrl = imageEntry?.attributes?.uri?.url ?? '';

  const basinId = element.relationships?.field_basin_name?.data?.id;
  const basinEntry = basinId ? includedById.get(basinId) : null;
  const basinName = basinEntry?.attributes?.name ?? '';

  const categoryId = element.relationships?.[categoryField]?.data?.id;
  const categoryEntry = categoryId ? includedById.get(categoryId) : null;
  const listingCategory = categoryEntry?.attributes?.name ?? '';

  return {
    occurrence_id: element.attributes?.title || '',
    created: element.attributes?.created || '',
    modified: element.attributes?.changed || '',
    occurrence_photo_url: `https://iriver.tw${occurrencePhotoUrl}`,
    processing_status: basinName,
    trail_name: basinName || '',
    other_location_info: '',
    report_date: dayjs(element.attributes?.created).format('YYYY-MM-DD') || '',
    report_time: dayjs(element.attributes?.created).format('HH:mm:ss') || '',
    latitude_y: element.attributes?.field_listing_map?.lat?.toString() || '',
    longitude_x: element.attributes?.field_listing_map?.lon?.toString() || '',
    occurrence_url: element.attributes?.drupal_internal__nid
      ? `https://iriver.tw/node/${element.attributes.drupal_internal__nid}`
      : '',
    occurrence: element.attributes?.field_listing_author || '',
    occurrence_status_Y: 0,
    occurrence_status_N: 0,
    listing_category: listingCategory,
  };
}

// 只取 mapListingItemToOccurrence 真正用到的欄位（sparse fieldset），
// 讓 Drupal 少組欄位、payload 從 ~900KB/頁 降到 ~320KB/頁，明顯加快回應。
const buildListingUsedFields = (categoryField: string) =>
  `title,created,changed,field_listing_map,field_listing_author,drupal_internal__nid,field_listing_image,field_basin_name,${categoryField}`;
// categoryField 因 content type 而異：listing 用 field_listing_category，
// advance_listing 用 field_listing_category_reference（兩者皆指向 taxonomy_term--listing_category）
const buildListingUrl = (
  base: string,
  nodeType: string,
  categoryField: string = 'field_listing_category',
) =>
  `${base}?include=field_listing_image,field_basin_name,${categoryField}` +
  `&fields%5Bnode--${nodeType}%5D=${buildListingUsedFields(categoryField)}` +
  `&fields%5Bfile--file%5D=uri` +
  `&fields%5Btaxonomy_term--basin_name%5D=name` +
  `&fields%5Btaxonomy_term--listing_category%5D=name` +
  `&page%5Blimit%5D=100`;

const cachedFetch = {
  counties: (): Promise<
    GeoJSON.FeatureCollection<GeoJSON.Polygon, GeoJSON.GeoJsonProperties>
  > => {
    if (memCache.has(CacheKey.COUNTY)) {
      return memCache.get(CacheKey.COUNTY);
    }
    // 快取 promise（而非解析後資料）以去重並發呼叫，避免同時多次抓 county.geojson
    const promise = fetch('./county.geojson').then((resp) => resp.json());
    memCache.set(CacheKey.COUNTY, promise);
    return promise;
  },
  towns: (): Promise<
    GeoJSON.FeatureCollection<GeoJSON.Polygon, GeoJSON.GeoJsonProperties>
  > => {
    if (memCache.has(CacheKey.TOWN)) {
      return memCache.get(CacheKey.TOWN);
    }
    // 快取 promise 以去重並發呼叫，避免 getCityAndTown 被 Promise.all 同時觸發時重抓 1.9MB town.geojson
    const promise = fetch('./town.geojson').then((resp) => resp.json());
    memCache.set(CacheKey.TOWN, promise);
    return promise;
  },
  trails: async (): Promise<Occurrence[]> => {
    if (memCache.has(CacheKey.TRAILS)) {
      return memCache.get(CacheKey.TRAILS);
    }

    const promise = (async (): Promise<Occurrence[]> => {
      const { data, includedById } = await fetchPaginatedListing(
        buildListingUrl('https://iriver.tw/jsonapi/node/listing', 'listing'),
        // 一次並行抓前 3 頁（涵蓋目前 ~261 筆）；若資料成長，fetchPaginatedListing 會依 meta.count 補抓其餘
        { initialOffsets: [0, 100, 200] },
      );
      const dataList: Occurrence[] = data.map((element) =>
        mapListingItemToOccurrence(element, includedById),
      );
      memCache.set(CacheKey.TRAILS, dataList);
      return dataList;
    })();
    memCache.set(CacheKey.TRAILS, promise);
    return promise;
  },
  advanceListing: async (): Promise<Occurrence[]> => {
    if (memCache.has(CacheKey.ADVANCE_LISTING)) {
      return memCache.get(CacheKey.ADVANCE_LISTING);
    }

    const promise = (async (): Promise<Occurrence[]> => {
      const { data, includedById } = await fetchPaginatedListing(
        buildListingUrl(
          'https://iriver.tw/zh-hant/jsonapi/node/advance_listing/',
          'advance_listing',
          'field_listing_category_reference',
        ),
      );
      const dataList: Occurrence[] = data.map((element) =>
        mapListingItemToOccurrence(element, includedById, 'field_listing_category_reference'),
      );
      memCache.set(CacheKey.ADVANCE_LISTING, dataList);
      return dataList;
    })();
    memCache.set(CacheKey.ADVANCE_LISTING, promise);
    return promise;
  },
};

router.registerRoute('GET', '/api/data', async (request, searchParams) => {
  // console.log(request, searchParams)
  // Handle GET request
  // Use searchParams.get('paramName') to access query parameters
  return new Response(JSON.stringify(icon.legend), { status: 200 });
});

function filterById(datas: Occurrence[], id: string | null): Occurrence[] {
  if (!id) return datas;
  return datas.filter((data) => data.occurrence_id.includes(id));
}

function filterByKeyword(datas: Occurrence[], keyword: string | null) {
  if (!keyword) return datas;
  return datas.filter(
    (data) =>
      data.trail_name.includes(keyword) ||
      data.occurrence_id.includes(keyword) ||
      data.occurrence.includes(keyword),
  );
}

function filterByProcessingStatus(datas: Occurrence[], processingStatus: string | null) {
  if (!processingStatus) return datas;
  return datas.filter((data) => data.processing_status === processingStatus);
}

function filterByBasinName(datas: Occurrence[], basinName: string | null) {
  if (!basinName) return datas;
  return datas.filter((data) => data.trail_name.includes(basinName));
}

function filterByOccurrence(datas: Occurrence[], occurrence: string | null) {
  if (!occurrence) return datas;
  return datas.filter((data) => data.occurrence.includes(occurrence));
}

function filterByListingCategory(datas: Occurrence[], category: string | null) {
  if (!category) return datas;
  return datas.filter((data) => data.listing_category === category);
}

async function filterByGeo(
  datas: Occurrence[],
  features: GeoJSON.Feature<GeoJSON.Polygon, GeoJSON.GeoJsonProperties>[],
  code: string | null,
) {
  if (!code) {
    return datas.filter((data) =>
      features.some((feature) =>
        booleanPointInPolygon([Number(data.longitude_x), Number(data.latitude_y)], feature),
      ),
    );
  }
  const [feature] = features.filter(
    (feature) => feature.properties?.COUNTYCODE === code || feature.properties?.TOWNCODE === code,
  );
  if (!feature) return datas;
  return datas.filter((data) =>
    booleanPointInPolygon([Number(data.longitude_x), Number(data.latitude_y)], feature),
  );
}

async function applyListingFilters(
  source: Occurrence[],
  searchParams: URLSearchParams,
): Promise<Occurrence[]> {
  const keyword = searchParams.get('keyword');
  const processingStatus = searchParams.get('processing_status');
  const county = searchParams.get('county');
  const town = searchParams.get('town');
  const id = searchParams.get('id');
  const basinName = searchParams.get('basin_name');
  const listingCategory = searchParams.get('listing_category');
  const occurrence = searchParams.get('occurrence');

  let datas = source;
  datas = filterById(datas, id);
  datas = filterByKeyword(datas, keyword);
  datas = filterByProcessingStatus(datas, processingStatus);
  datas = filterByBasinName(datas, basinName);
  datas = filterByListingCategory(datas, listingCategory);
  datas = filterByOccurrence(datas, occurrence);

  if (!county && !town) {
    const { features } = await cachedFetch.counties();
    datas = await filterByGeo(datas, features, null);
  }

  if (county && !town) {
    const { features } = await cachedFetch.counties();
    datas = await filterByGeo(datas, features, county);
  }

  if (town) {
    const { features } = await cachedFetch.towns();
    datas = await filterByGeo(datas, features, town);
  }

  return datas;
}

router.registerRoute('GET', '/Trails', async (request, searchParams) => {
  const datas = await applyListingFilters(await cachedFetch.trails(), searchParams);
  const geojson = featureCollection(
    datas.map((data) => point([Number(data.longitude_x), Number(data.latitude_y)], { ...data })),
  );
  return new Response(JSON.stringify(geojson), { status: 200 });
});

router.registerRoute('GET', '/AdvanceListing', async (request, searchParams) => {
  const datas = await applyListingFilters(await cachedFetch.advanceListing(), searchParams);
  const geojson = featureCollection(
    datas.map((data) => point([Number(data.longitude_x), Number(data.latitude_y)], { ...data })),
  );
  return new Response(JSON.stringify(geojson), { status: 200 });
});

router.registerRoute('GET', '/Region/counties', async (request) => {
  const data = await cachedFetch.counties();
  return new Response(
    JSON.stringify(
      data.features.map((feature) => ({
        countyname: feature.properties?.COUNTYNAME,
        countycode: feature.properties?.COUNTYCODE,
      })),
    ),
    { status: 200 },
  );
});

router.registerRoute('GET', '/Region/towns', async (request, searchParam) => {
  if (!searchParam.has('countyCode')) {
    return new Response(JSON.stringify([]), { status: 200 });
  }
  const countyCode = searchParam.get('countyCode');
  const data = await cachedFetch.towns();
  // 過濾副本，不能改到 cachedFetch.towns() 回傳的快取物件本身，
  // 否則快取會被永久砍成只剩該縣市，害 getCityAndTown 反查失準。
  const features = countyCode
    ? data.features.filter((feature) => feature.properties?.COUNTYCODE === countyCode)
    : data.features;
  return new Response(
    JSON.stringify(
      features.map((feature) => ({
        townname: feature.properties?.TOWNNAME,
        towncode: feature.properties?.TOWNCODE,
      })),
    ),
    { status: 200 },
  );
});

self.addEventListener('fetch', (event: FetchEvent) => {
  router.handleFetchEvent(event);
});

self.addEventListener('install', (event) => {
  self.skipWaiting();
  console.log('WORKER: install event in progress.');
});

self.addEventListener('activate', (event) => {
  clients.claim();
  console.log('WORKER: activate event in progress.');
});

export async function getCityAndTown(
  longitude: number,
  latitude: number,
): Promise<{ city: string; town: string }> {
  const counties = await cachedFetch.counties();
  const towns = await cachedFetch.towns();

  let city = '';
  let town = '';

  for (const feature of counties.features) {
    if (booleanPointInPolygon([longitude, latitude], feature)) {
      city = feature.properties?.COUNTYNAME || '';
      break;
    }
  }

  for (const feature of towns.features) {
    if (booleanPointInPolygon([longitude, latitude], feature)) {
      town = feature.properties?.TOWNNAME || '';
      break;
    }
  }

  return { city, town };
}
